import { USE_DUMMY_GAME_DATA } from '@/shared/config/env';
import { processRequest, RequestMethod } from '@/shared/services/api';

import bouncySumsDummyData from '@/features/bouncySums/bouncySumsDummyData.json';

import type { Question } from '@/features/gameSession/types';

const BOUNCY_SUMS_QUESTIONS_ENDPOINT = 'user_learning_path_section';

type BouncySumsQuestionsResponse = {
	user_learning_path_section?: {
		questions?: Question[];
	};
};

const getDummyQuestionTables = (playlistSectionIds: number[]) => {
	const selectedPlaylistSectionIds = new Set(playlistSectionIds);

	return Object.entries(bouncySumsDummyData.playlist_sections)
		.filter(([, playlistSectionId]) => selectedPlaylistSectionIds.has(Number(playlistSectionId)))
		.map(([table]) => Number(table))
		.filter((table) => Number.isInteger(table) && table >= 1 && table <= 10)
		.sort((a, b) => a - b);
};

const createDummyQuestion = (table: number, multiplier: number, index: number): Question => {
	const question = `${table}x${multiplier}=`;
	const answer = String(table * multiplier);

	return {
		id: 880000 + index,
		ai_tutor_enabled: false,
		explanation_available: false,
		question_display: question,
		time_estimate_in_seconds: 0,
		title: question,
		topic_id: 0,
		topic_title: bouncySumsDummyData.title,
		type: 'Question::OpenEnded',
		answers: [
			{
				id: `${table}x${multiplier}`,
				answer_display: answer,
				answer_text: answer,
				is_correct: true,
			},
		],
	};
};

const createDummyQuestions = (playlistSectionIds: number[]) => {
	const tables = getDummyQuestionTables(playlistSectionIds);

	return tables.flatMap((table, tableIndex) =>
		Array.from({ length: 10 }, (_, index) => createDummyQuestion(table, index + 1, tableIndex * 10 + index)),
	);
};

export async function fetchBouncySumsQuestions(playlistSectionIds: number[]): Promise<Question[]> {
	if (USE_DUMMY_GAME_DATA) {
		return createDummyQuestions(playlistSectionIds);
	}

	if (!playlistSectionIds.length) {
		return [];
	}

	const response = await processRequest(BOUNCY_SUMS_QUESTIONS_ENDPOINT, RequestMethod.POST, {
		playlist_section_ids: playlistSectionIds,
	});
	const responseData = response.data as BouncySumsQuestionsResponse;
	const questions = responseData.user_learning_path_section?.questions;

	return Array.isArray(questions) ? questions : [];
}
